import Cookies from "js-cookie";
import { assoc, has, isNil } from "ramda";

import { TOKEN_EXPIRE, TOKEN_IN_COOKIE } from "@/libs/constants";

import { localSet } from "./storage";

export const checkFileType = (fileType: string): boolean => {
  const allowedTypes = [
    "video/mp4",
    "image/jpeg",
    "image/png",
    "application/pdf",
  ];

  return allowedTypes.includes(fileType);
};
// 判断上传类型
export const judgmentType = (
  fileType: string
): "" | "AUDIO" | "IMG" | "VIDEO" | "PDF" => {
  const videoTypes = ["video/mp4", "video/quicktime"];

  const pdfTypes = ["application/pdf"];

  const imageTypes = ["image/jpeg", "image/png"];

  const audioTypes = ["audio/mpeg", "audio/mp3"];

  if (videoTypes.includes(fileType)) {
    // 文件类型是mp4
    return "VIDEO";
  } else if (pdfTypes.includes(fileType)) {
    // 文件类型是pdf
    return "PDF";
  } else if (audioTypes.includes(fileType)) {
    // 文件类型是pdf
    return "AUDIO";
  } else if (imageTypes.includes(fileType)) {
    // 文件类型是img
    return "IMG";
  } else {
    // 文件类型不在允许的类型列表中
    return "";
  }
};
// 防抖

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout | null = null;

  // 添加类型断言，明确返回函数的上下文类型为 'any' 或具体类型（如果需要）
  return function (this: any, ...args: Parameters<T>) {
    // 如果存在定时器，则清除它
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    // 设置新的定时器
    timeoutId = setTimeout(() => {
      // 调用原始函数，并传递所有参数
      func.apply(this, args);
      // 清空定时器ID，防止内存泄漏
      timeoutId = null;
    }, delay);
  } as (...args: Parameters<T>) => void;
};

// 根据token 获取有效期
export const getTokenExp = (token: string) => {
  try {
    const base64Url = token.split(".")[1];

    const base64 = base64Url.replace("-", "+").replace("_", "/");

    const decodedToken = JSON.parse(atob(base64));

    if (decodedToken && decodedToken.exp) {
      return decodedToken.exp * 1000;
    }

    return null;
  } catch (error) {
    return null;
  }
};

export const saveTokenExpire = () => {
  const token = Cookies.get(TOKEN_IN_COOKIE);

  if (token) {
    const tokenInfo = token.split(" ");

    // 解析token，将其分为头部、载荷和签名
    const parts = tokenInfo[1].split(".");

    const payload = JSON.parse(atob(parts[1]));

    if (payload.exp) {
      // 获取有效期信息
      const expire = payload.exp * 1000; // 转换为毫秒级时间戳

      localSet(TOKEN_EXPIRE, expire);
    }
  }
};

export const getFilenameFromPath = (path: string) => {
  // 无论是正斜杠(/)还是反斜杠(\)，都将其统一分割
  // 使用正则表达式匹配两种斜杠
  const parts = path.split(/[\\/]/);

  // 获取分割后的最后一部分
  return parts.pop();
};

/**
 * 將 xx:xx:xx 格式的時間字串轉換為總秒數
 * @param timeString - 時間字串，格式為 HH:MM:SS、H:M:S、MM:SS 或 SS
 * @returns 總秒數
 * @throws {Error} 當輸入格式無效時拋出錯誤
 */
export const parseTimeStringToSeconds = (timeString: string): number => {
  // 移除首尾空白
  const trimmedStr = timeString.trim();

  // 驗證基本格式：至少包含一個數字和可選的冒號分隔符
  if (!/^[\d:]+$/.test(trimmedStr)) {
    throw new Error(`無效的時間格式 "${timeString}"：只能包含數字和冒號`);
  }

  // 分割時間部分
  const parts = trimmedStr.split(":");

  // 驗證分割後的數量（支援1-3個部分）
  if (parts.length < 1 || parts.length > 3) {
    throw new Error(`無效的時間格式 "${timeString}"：應為1-3個部分用冒號分隔`);
  }

  // 驗證每個部分都是有效的整數
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (part === "") {
      throw new Error(`無效的時間格式 "${timeString}"：第${i + 1}部分為空`);
    }

    const num = parseInt(part, 10);

    if (isNaN(num) || num < 0) {
      throw new Error(`無效的時間值 "${part}"：必須是非負整數`);
    }

    // 檢查分鐘和秒數是否在合理範圍內（可選）
    if (i === parts.length - 2 && num >= 60) {
      // MM部分（倒數第二部分）
      console.warn(`警告：分鐘值 ${num} >=60，可能不是標準時間表示法`);
    }
    if (i === parts.length - 1 && num >= 60) {
      // SS部分（最後一部分）
      console.warn(`警告：秒鐘值 ${num} >=60，可能不是標準時間表示法`);
    }

    parts[i] = num.toString(); // 確保是數字字串
  }

  let hours = 0;

  let minutes = 0;

  let seconds = parseInt(parts[parts.length - 1], 10);

  switch (parts.length) {
    case 3: // HH:MM:SS
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
      seconds = parseInt(parts[2], 10);
      break;

    case 2: // MM:SS（預設小時為0）
      minutes = parseInt(parts[0], 10);
      seconds = parseInt(parts[1], 10);
      break;

    case 1: // SS（預設小時和分鐘為0）
      seconds = parseInt(parts[0], 10);
      break;

    default:
      throw new Error("無法解析的時間格式");
  }

  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * **增強版**：支援多種時間格式的轉換函數
 */
export const convertTimeFormatToSeconds = (
  timeInput:
    | string
    | number
    | { hours?: number; minutes?: number; seconds?: number }
): number => {
  if (typeof timeInput === "string") {
    return parseTimeStringToSeconds(timeInput);
  } else if (typeof timeInput === "number") {
    return Math.floor(timeInput); // API設計決策：如果是數字，直接返回整數秒
  } else if (typeof timeInput === "object" && timeInput !== null) {
    const { hours = 0, minutes = 0, seconds = 0 } = timeInput;

    if (
      [hours, minutes, seconds].some(
        (val) => typeof val !== "number" || isNaN(val)
      )
    ) {
      throw new Error("物件參數中的時分秒必須是有效數字");
    }

    return (
      Math.floor(hours) * 3600 + Math.floor(minutes) * 60 + Math.floor(seconds)
    );
  } else {
    throw new TypeError("不支援的輸入類型");
  }
};
/**
 * 加载更多
 * @param getMore 获取更多数据的方法
 * @returns IntersectionObserver
 */
export const loadMore = (getMore: () => void) => {
  return new IntersectionObserver(
    (entries) => {
      const first = entries[0];
      // 当加载元素进入视口且未正在加载时，触发加载更多

      if (first.isIntersecting) {
        getMore();
      }
    },
    {
      root: null, // 使用浏览器视口作为根
      rootMargin: "100px", // 提前 100px 开始加载
      threshold: 0, // 只要有一个像素可见就触发
    }
  );
};

// 以下是兩個函數，分別實現蛇形（snake_case）與小駝峰（camelCase）之間的相互轉換，並支援嵌套對象和數組：

/**
 * 將字符串從蛇形轉換為小駝峰
 */
export const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
};

/**
 * 將字符串從小駝峰轉換為蛇形
 */
export const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * 遞歸遍歷物件或陣列，將所有鍵從蛇形轉為小駝峰
 */

export const convertKeysToCamel = (
  obj: Record<string, unknown>[] | Record<string, unknown> | any
): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToCamel(item));
  }

  if (!isNil(obj) && typeof obj === "object") {
    const newObj = {} as Record<string, unknown>;

    for (const key in obj) {
      if (has(key, obj)) {
        const newKey = snakeToCamel(key);

        newObj[newKey] = convertKeysToCamel(obj[key]);
      }
    }

    return newObj;
  }

  return obj;
};

/**
 * 遞歸遍歷物件或陣列，將所有鍵從小駝峰轉為蛇形
 */
export const convertKeysToSnake = (
  obj: Record<string, unknown>[] | Record<string, unknown> | any
): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertKeysToSnake(item));
  }

  if (obj !== null && typeof obj === "object") {
    const newObj: Record<string, unknown> = {};

    for (const key in obj) {
      if (has(key, obj)) {
        const newKey = camelToSnake(key);

        newObj[newKey] = convertKeysToSnake(obj[key]);
      }
    }

    return newObj;
  }

  return obj;
};

/** 判斷數組是否為空（null / undefined / 長度為 0） */
export const isEmptyArray = (arr: unknown[] | null | undefined): boolean => {
  return !arr || arr.length === 0;
};
